import type { StateCreator } from "zustand";
import type { TestType } from "@/enums/types";

export interface CameraPermissionSliceState {
  acceptedCameraPermissions: Record<string, TestType[]>;
}

interface CameraPermissionSliceActions {
  addAcceptedCameraPermission: (processId: string | number, testType: TestType) => void;
  hasAcceptedCameraPermission: (processId: string | number, testType: TestType) => boolean;
  clearAcceptedCameraPermissions: () => void;
  removeProcessCameraPermissions: (processId: string | number) => void;
}

export type CameraPermissionSlice = CameraPermissionSliceState & CameraPermissionSliceActions;

export const createCameraPermissionSlice: StateCreator<
  CameraPermissionSlice,
  [["zustand/devtools", never]],
  [],
  CameraPermissionSlice
> = (set, get) => ({
  acceptedCameraPermissions: {},
  addAcceptedCameraPermission: (processId, testType) =>
    set((state) => {
      const key = String(processId);
      const existingPermissions = state.acceptedCameraPermissions[key] || [];

      // Check if testType already exists for this process
      if (existingPermissions.includes(testType)) {
        return state;
      }

      return {
        acceptedCameraPermissions: {
          ...state.acceptedCameraPermissions,
          [key]: [...existingPermissions, testType],
        },
      };
    }),
  hasAcceptedCameraPermission: (processId, testType) => {
    const key = String(processId);
    const permissions = get().acceptedCameraPermissions[key];
    return permissions ? permissions.includes(testType) : false;
  },
  clearAcceptedCameraPermissions: () =>
    set({
      acceptedCameraPermissions: {},
    }),
  removeProcessCameraPermissions: (processId) =>
    set((state) => {
      const key = String(processId);

      // If the processId doesn't exist, return current state unchanged
      if (!state.acceptedCameraPermissions[key]) {
        return state;
      }

      // Create a new object without the specified processId
      const { [key]: _, ...remainingPermissions } = state.acceptedCameraPermissions;

      return {
        acceptedCameraPermissions: remainingPermissions,
      };
    }),
});
